/**
 * @module prop_types
 *
 * Built-in PropTypes + re-usable custom React component prop types.
 */
import PropTypes from 'prop-types';

/**
 * A "translation" is an object containing all the translated attributes for a
 * given entity for a given language. It must contain an attribute called
 * `languageId` which is the database Id for the language in our API. All other
 * attributes must be translated strings for this language.
 *
 * The purpose of this structure is to create a common, consistent, pattern for
 * storing translations strings for arbitrary attributes for any entity in the
 * app. This structure makes it much easier to scale (e.g., adding different
 * new translation attribute strings + referencing default values and db ids) and
 * more predictable when working with translations in the UI.
 *
 * @example
 * 'en-ca': {
 *   name: 'My English Name',
 *   description: 'A description for my English Name.',
 *   other: 'Some other stuff that is translated to English.'
 * }
 */
// TODO: validate that `languageId` specifically is a number, and that all other
// attributes are strings.
const translation = PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]));

/**
 * An entity may contain a set of "translations" which is a dictionary where
 * each key is a language's ISO code. The value of each element is a "translation"
 * object (see above).
 *
 * Validates that ISO codes are supported by the app (from settings) and that
 * each set of translations match the "translations" type above.
 *
 * @example
 * translations: {
 *   'en-ca': {
 *      name: 'My English Name',
 *      description: 'A description for my English Name.',
 *      other: 'Some other stuff that is translated to English.'
 *    },
 *    'fr-ca': {
 *      name: 'Mon nom Francais',
 *      description: 'Un description pour mon nom Francais.',
 *      other: 'Quelque chose d'autre en Francais.'
 *    }
 *  }
 */
// TODO: validate that the only attributes in a `translations` block are named
// ISO language codes and that they are defined in `settings.supportedLanguages`.
const translations = PropTypes.objectOf(translation);

const sortOrder = PropTypes.oneOf(['asc', 'desc']);

const selectableValue = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

// Used by Select & SelectMultiple components.
const selectable = PropTypes.shape({
  value: selectableValue,
  label: PropTypes.string,
  disabled: PropTypes.bool,
});

const user = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
});

const survey = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  surveyTypeId: PropTypes.number,
});

const questionLogic = PropTypes.shape({
  id: PropTypes.number,
  surveyId: PropTypes.number,
  mainSurveyElementId: PropTypes.number,
  affectedSurveyElementId: PropTypes.number,
  rating: PropTypes.number,
  surveyElementQuestionOptionsChoiceId: PropTypes.number,
  isEqualsTo: PropTypes.bool,
  isHigherThan: PropTypes.bool,
  isLowerThan: PropTypes.bool,
});

const surveyElement = PropTypes.shape({
  id: PropTypes.number,
  title: PropTypes.string,
  order: PropTypes.number,
  orderIndex: PropTypes.number,
  typeName: PropTypes.string,
  maxLabel: PropTypes.string,
  minLabel: PropTypes.string,
  maxOptions: PropTypes.number,
  minOptions: PropTypes.number,
  iconUrl: PropTypes.string,
  // @ts-expect-error TS(2339): Property 'iconName' does not exist on type 'typeof... Remove this comment to see the full error message
  iconName: PropTypes.iconName,
  options: PropTypes.arrayOf(selectable),
});

const surveyEmailLanguage = PropTypes.shape({
  languageId: PropTypes.number,
  languageCode: PropTypes.string,
  subject: PropTypes.string,
  content: PropTypes.string,
});

const trendRatingMeasurement = PropTypes.shape({
  id: PropTypes.string,
  categoryId: PropTypes.number,
  questionId: PropTypes.number,
  type: PropTypes.string,
  group: PropTypes.string,
  position: PropTypes.number,
  surveys: PropTypes.arrayOf(survey),
  label: PropTypes.string,
  percentFavorable: PropTypes.number,
  percentNeutral: PropTypes.number,
  percentUnfavorable: PropTypes.number,
  scoreAverage: PropTypes.number,
  enpsScore: PropTypes.number,
  countFavorable: PropTypes.number,
  countNeutral: PropTypes.number,
  countUnfavorable: PropTypes.number,
});

const optionResponse = PropTypes.shape({
  id: PropTypes.string,
  rating: PropTypes.number,
  choiceId: PropTypes.number,
  respondentPercent: PropTypes.number,
  totalRespondents: PropTypes.number,
});

const category = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  isKeyFactor: PropTypes.bool,
  translations: translations,
});

const learningContent = PropTypes.shape({
  id: PropTypes.number,
  title: PropTypes.string,
  effectiveness: PropTypes.number,
  categories: PropTypes.arrayOf(category),
});

const learningContentParams = PropTypes.shape({
  filterBy: PropTypes.shape({
    contentType: PropTypes.string,
    tagIds: PropTypes.arrayOf(PropTypes.number),
    categoryIds: PropTypes.arrayOf(PropTypes.number),
  }),
  search: PropTypes.string,
  orderBy: PropTypes.string,
  order: sortOrder,
});

// NOTE: `categories` can be either a normalized array of category IDs, or an
// array of denormalized category objects.
const surveyType = PropTypes.shape({
  id: PropTypes.number,
  companyId: PropTypes.number,
  baseSurveyTypeId: PropTypes.number,
  name: PropTypes.string,
  threshold: PropTypes.number,
  categories: PropTypes.oneOfType([PropTypes.arrayOf(category), PropTypes.arrayOf(PropTypes.number)]),
  translations: translations,
});

const task = PropTypes.shape({
  id: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  effectiveness: PropTypes.number,
  surveyTypeId: PropTypes.number,
  users: PropTypes.arrayOf(user),
  surveys: PropTypes.arrayOf(survey),
  categories: PropTypes.arrayOf(category),
  isComplete: PropTypes.bool,
  status: PropTypes.string,
  dueAt: PropTypes.string,
});

const tableColumn = PropTypes.shape({
  headerName: PropTypes.string,
  allowSort: PropTypes.bool,
  orderBy: PropTypes.string,
  headerClassName: PropTypes.string,
  renderHeader: PropTypes.func,
  cellKey: PropTypes.string,
  cellClassName: PropTypes.string,
  renderCell: PropTypes.func,
});

const tableColumns = PropTypes.arrayOf(tableColumn);

const template = PropTypes.shape({
  title: PropTypes.string,
  description: PropTypes.string,
});

const attributeValueResult = PropTypes.shape({
  id: PropTypes.string,
  attributeValue: PropTypes.string,
  attributeValueLabel: PropTypes.string,
  recipientCount: PropTypes.number,
  respondentCount: PropTypes.number,
  respondentPercent: PropTypes.number,
});

const attributeResult = PropTypes.shape({
  attributeId: PropTypes.number,
  attributeValueResults: PropTypes.arrayOf(attributeValueResult),
  isHierarchyAttribute: PropTypes.bool,
});

const distributionResult = PropTypes.shape({
  unfavorablePercent: PropTypes.number,
  neutralPercent: PropTypes.number,
  favorablePercent: PropTypes.number,
});

const insightResult = PropTypes.shape({
  hasIncreasedSignificantly: PropTypes.bool,
  hasDecreasedSignificantly: PropTypes.bool,
  differCompareToLastMeasured: PropTypes.number,
  isTopOrgWide: PropTypes.bool,
  isAboveOrgWide: PropTypes.bool,
  isBottomOrgWide: PropTypes.bool,
  isBelowOrgWide: PropTypes.bool,
  differCompareToOrgWide: PropTypes.number,
});

const surveyElementResult = PropTypes.shape({
  surveyElementId: PropTypes.number,
  isAnonymized: PropTypes.bool,
  isKey: PropTypes.bool,
  enpsDistribution: distributionResult,
  enpsScore: PropTypes.number,
  scaleDistribution: distributionResult,
  scoreAverage: PropTypes.number,
  attributeResults: PropTypes.arrayOf(attributeResult),
  insight: insightResult,
});

const categoryResult = PropTypes.shape({
  categoryId: PropTypes.number,
  isAnonymized: PropTypes.bool,
  isKey: PropTypes.bool,
  enpsDistribution: distributionResult,
  enpsScore: PropTypes.number,
  scaleDistribution: distributionResult,
  scoreAverage: PropTypes.number,
  surveyElementResults: PropTypes.arrayOf(surveyElementResult),
  attributeResults: PropTypes.arrayOf(attributeResult),
  insight: insightResult,
});

const insightMeta = PropTypes.shape({
  lastMeasured: PropTypes.string,
  differCompareToLastMeasured: PropTypes.number,
  containLastMeasuredDiffer: PropTypes.bool,
  containOrgWideDiffer: PropTypes.bool,
  containOrgWideRank: PropTypes.bool,
});

export const propTypes = {
  ...PropTypes,
  attributeResult,
  attributeValueResult,
  category,
  categoryResult,
  distributionResult,
  insightMeta,
  learningContent,
  learningContentParams,
  optionResponse,
  questionLogic,
  selectable,
  selectableValue,
  sortOrder,
  survey,
  surveyElement,
  surveyElementResult,
  surveyEmailLanguage,
  surveyType,
  task,
  tableColumn,
  tableColumns,
  template,
  translation,
  translations,
  trendRatingMeasurement,
  user,
};
